import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../lib/api';

export default function QuestionFormModal({
    question,
    onSave,
    onClose,
    category,
    onNavigate,
    hasPrevious = false,
    hasNext = false,
    currentIndex = null,
    totalCount = 0,
    isEditing = false,
    onQuestionUpdated = () => { },
}) {
    const buildFormState = (source) => {
        const baseChoices = Array.isArray(source?.choices) ? [...source.choices] : [];

        if (!source) {
            while (baseChoices.length < 4) {
                baseChoices.push("");
            }
        } else {
            while (baseChoices.length < 2) {
                baseChoices.push("");
            }
        }

        const baseHighlights = Array.isArray(source?.answerHighlights)
            ? source.answerHighlights.map((value) => (value === "__CLEARED__" ? "" : value))
            : [];
        const normalizedHighlights = baseChoices.map((_, idx) => baseHighlights[idx] || "");

        const normalizedCorrectIndex = typeof source?.correctIndex === 'number' && source.correctIndex >= 0 && source.correctIndex < baseChoices.length
            ? source.correctIndex
            : 0;

        const baseImagePath = typeof source?.explanationImagePath === 'string' ? source.explanationImagePath : '';
        const baseImageUrl = source?.explanationImageUrl
            ? ((source.explanationImageUrl.startsWith('http') || source.explanationImageUrl.startsWith('data:'))
                ? source.explanationImageUrl
                : `${API_BASE}${source.explanationImageUrl}`)
            : (baseImagePath ? `${API_BASE}/uploads/exam-explanations/${baseImagePath}` : '');

        return {
            text: source?.text || "",
            choices: baseChoices,
            correctIndex: normalizedCorrectIndex,
            explanation: source?.explanation || "",
            questionHighlight: source?.questionHighlight === "__CLEARED__" ? "" : (source?.questionHighlight || ""),
            answerHighlights: normalizedHighlights,
            explanationImagePath: baseImagePath,
            explanationImageUrl: baseImageUrl,
        };
    };

    const [formData, setFormData] = useState(buildFormState(question));
    const [isDirty, setIsDirty] = useState(false);
    const [isUploadingExplanationImage, setIsUploadingExplanationImage] = useState(false);
    const [explanationImageError, setExplanationImageError] = useState('');
    const explanationImagePreviewUrl = formData.explanationImageUrl || '';
    const hasExplanationImage = Boolean(formData.explanationImagePath);

    useEffect(() => {
        setFormData(buildFormState(question));
        setIsDirty(false);
        setExplanationImageError('');
        setIsUploadingExplanationImage(false);
    }, [question, category]);

    const handleSubmit = async (event, options = {}) => {
        if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }

        const { closeAfterSave = true, skipSuccessAlert = false } = options;

        const trimmedQuestionText = formData.text.trim();
        if (!trimmedQuestionText) {
            alert("Question text is required");
            return false;
        }

        const preparedChoices = formData.choices.map((choice, idx) => ({
            text: choice.trim(),
            highlight: (formData.answerHighlights[idx] || "").trim(),
            originalIdx: idx,
        }));

        const filteredChoices = preparedChoices.filter(({ text }) => text.length > 0);

        if (filteredChoices.length < 2) {
            alert("At least two answer choices are required");
            return false;
        }

        const sanitizedChoices = filteredChoices.map(({ text }) => text);
        const sanitizedHighlights = filteredChoices.map(({ highlight, originalIdx }) => {
            const originalHighlight = question?.answerHighlights?.[originalIdx] || "";
            if (!highlight && originalHighlight && originalHighlight.trim()) {
                return "__CLEARED__";
            }
            return highlight;
        });

        let sanitizedCorrectIndex = formData.correctIndex;
        if (sanitizedCorrectIndex >= sanitizedChoices.length) {
            sanitizedCorrectIndex = sanitizedChoices.length - 1;
        }
        if (sanitizedCorrectIndex < 0) sanitizedCorrectIndex = 0;

        let questionHighlightPayload = formData.questionHighlight.trim();
        if (!questionHighlightPayload && question?.questionHighlight && question.questionHighlight.trim()) {
            questionHighlightPayload = "__CLEARED__";
        }

        const payload = {
            text: trimmedQuestionText,
            choices: sanitizedChoices,
            correctIndex: sanitizedCorrectIndex,
            explanation: formData.explanation.trim(),
            questionHighlight: questionHighlightPayload,
            answerHighlights: sanitizedHighlights,
            // Keep image edits separate (handled via /explanation-image upload endpoint).
            // IMPORTANT: do not send explanationImageUrl here because it can be a huge data: URL,
            // which causes 413 (request entity too large) on many hosts.
            explanationImagePath: formData.explanationImagePath || '',
        };

        const result = await onSave(payload, { skipSuccessAlert });
        const wasSuccessful = Boolean(result?.success);

        if (wasSuccessful) {
            const savedQuestion = result?.question;
            if (savedQuestion) {
                onQuestionUpdated(savedQuestion);
            }
            if (!closeAfterSave) {
                const nextState = savedQuestion
                    ? buildFormState(savedQuestion)
                    : buildFormState({
                        text: payload.text,
                        choices: payload.choices,
                        correctIndex: payload.correctIndex,
                        explanation: payload.explanation,
                        questionHighlight: payload.questionHighlight,
                        answerHighlights: payload.answerHighlights,
                        explanationImagePath: payload.explanationImagePath,
                    });
                setFormData(nextState);
            }

            setIsDirty(false);
            if (closeAfterSave) {
                onClose();
            }
        }

        return wasSuccessful;
    };

    const markDirty = () => setIsDirty(true);

    const handleExplanationImageUpload = async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        if (!isEditing || !question?.id) {
            alert('Please save the question before uploading an explanation image.');
            event.target.value = '';
            return;
        }

        setExplanationImageError('');

        if (!file.type.startsWith('image/')) {
            setExplanationImageError('Please select an image file.');
            event.target.value = '';
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setExplanationImageError('Image must be 5 MB or smaller.');
            event.target.value = '';
            return;
        }

        const formDataUpload = new FormData();
        formDataUpload.append('image', file);

        setIsUploadingExplanationImage(true);
        try {
            const response = await fetch(`${API_BASE}/api/exams/questions/${question.id}/explanation-image`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
                body: formDataUpload,
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.error || 'Failed to upload explanation image');
            }

            if (data?.question) {
                const imageState = buildFormState(data.question);
                setFormData(prev => ({
                    ...prev,
                    explanationImagePath: imageState.explanationImagePath,
                    explanationImageUrl: imageState.explanationImageUrl,
                }));
                onQuestionUpdated(data.question);
            }
        } catch (err) {
            console.error('Explanation image upload failed:', err);
            setExplanationImageError(err?.message || 'Failed to upload explanation image');
        } finally {
            setIsUploadingExplanationImage(false);
            if (event.target) {
                event.target.value = '';
            }
        }
    };

    const handleExplanationImageRemove = async () => {
        if (!isEditing || !question?.id) return;
        setExplanationImageError('');
        setIsUploadingExplanationImage(true);
        try {
            const response = await fetch(`${API_BASE}/api/exams/questions/${question.id}/explanation-image`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.error || 'Failed to remove explanation image');
            }

            const updatedQuestion = data?.question || { ...question, explanationImagePath: null, explanationImageUrl: null };
            const imageState = buildFormState(updatedQuestion);
            setFormData(prev => ({
                ...prev,
                explanationImagePath: imageState.explanationImagePath,
                explanationImageUrl: imageState.explanationImageUrl,
            }));
            onQuestionUpdated(updatedQuestion);
        } catch (err) {
            console.error('Failed to remove explanation image:', err);
            setExplanationImageError(err?.message || 'Failed to remove explanation image');
        } finally {
            setIsUploadingExplanationImage(false);
        }
    };

    const updateChoice = (index, value) => {
        setFormData((prev) => {
            const newChoices = [...prev.choices];
            if (index < 0 || index >= newChoices.length) return prev;
            newChoices[index] = value;
            const newAnswerHighlights = [...prev.answerHighlights];
            if (index >= newAnswerHighlights.length) {
                newAnswerHighlights[index] = "";
            }
            return { ...prev, choices: newChoices, answerHighlights: newAnswerHighlights };
        });
        markDirty();
    };

    const updateAnswerHighlight = (index, value) => {
        setFormData((prev) => {
            const newAnswerHighlights = [...prev.answerHighlights];
            if (index < 0 || index >= newAnswerHighlights.length) return prev;
            newAnswerHighlights[index] = value;
            return { ...prev, answerHighlights: newAnswerHighlights };
        });
        markDirty();
    };

    const handleFieldChange = (field) => (event) => {
        const value = event.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
        markDirty();
    };

    const handleCorrectIndexChange = (event) => {
        const rawValue = Number(event.target.value);
        const value = Number.isFinite(rawValue) ? rawValue : 0;
        const clampedValue = Math.min(Math.max(value, 0), formData.choices.length - 1);
        setFormData((prev) => ({ ...prev, correctIndex: clampedValue }));
        markDirty();
    };

    const handleNavigateClick = async (direction) => {
        if (!onNavigate) return;
        if ((direction === 'prev' && !hasPrevious) || (direction === 'next' && !hasNext)) return;

        if (isDirty) {
            const shouldSave = window.confirm('You have unsaved changes. Save before moving to another question? Click OK to save, or Cancel to discard changes.');
            if (shouldSave) {
                const success = await handleSubmit(undefined, { closeAfterSave: false, skipSuccessAlert: true });
                if (!success) {
                    return;
                }
            } else {
                setIsDirty(false);
            }
        }

        onNavigate(direction);
    };

    const addChoice = () => {
        setFormData((prev) => {
            if (prev.choices.length >= 4) return prev;
            return {
                ...prev,
                choices: [...prev.choices, ""],
                answerHighlights: [...prev.answerHighlights, ""],
            };
        });
        markDirty();
    };

    const removeChoice = (index) => {
        setFormData((prev) => {
            if (prev.choices.length <= 2) return prev;
            if (index < 0 || index >= prev.choices.length) return prev;

            const choices = prev.choices.filter((_, i) => i !== index);
            const answerHighlights = prev.answerHighlights.filter((_, i) => i !== index);

            let correctIndex = prev.correctIndex;
            if (correctIndex === index) {
                correctIndex = 0;
            } else if (correctIndex > index) {
                correctIndex -= 1;
            }
            if (correctIndex >= choices.length) {
                correctIndex = choices.length - 1;
            }

            return {
                ...prev,
                choices,
                answerHighlights,
                correctIndex: Math.max(0, correctIndex),
            };
        });
        markDirty();
    };

    return (
        <div className="fixed inset-0 z-[10000] flex items-start justify-center bg-black/60 pt-20">
            <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-6 gap-4">
                    <div>
                        <h3 className="text-xl font-semibold">
                            {isEditing ? "Edit Question" : "Add New Question"}
                        </h3>
                        {isEditing && currentIndex !== null && totalCount > 0 && (
                            <p className="text-sm text-slate-500">Question {currentIndex + 1} of {totalCount}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {isEditing && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => handleNavigateClick('prev')}
                                    disabled={!hasPrevious}
                                    className={`px-3 py-1 rounded-lg border ${hasPrevious ? 'text-slate-700 hover:bg-slate-100' : 'text-slate-400 border-slate-200 cursor-not-allowed'}`}
                                >
                                    ← Previous
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleNavigateClick('next')}
                                    disabled={!hasNext}
                                    className={`px-3 py-1 rounded-lg border ${hasNext ? 'text-slate-700 hover:bg-slate-100' : 'text-slate-400 border-slate-200 cursor-not-allowed'}`}
                                >
                                    Next →
                                </button>
                            </>
                        )}
                        <button onClick={onClose} className="text-slate-500 hover:text-slate-900">✕</button>
                    </div>
                </div>

                <form onSubmit={(event) => handleSubmit(event)} className="space-y-4">
                    {/* Category Display */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Category:</label>
                        <div className="text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
                            {category}
                        </div>
                    </div>

                    {/* Question Text */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Question Text:</label>
                        <textarea
                            value={formData.text}
                            onChange={handleFieldChange('text')}
                            className="w-full rounded-lg border px-3 py-2 h-24 resize-none"
                            placeholder="Enter your question here..."
                            required
                        />
                    </div>

                    {/* Question Highlight */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Question Highlight (Optional):</label>
                        <input
                            type="text"
                            value={formData.questionHighlight}
                            onChange={handleFieldChange('questionHighlight')}
                            className="w-full rounded-lg border px-3 py-2"
                            placeholder="Enter text to highlight in the question (e.g., 'air pressure')"
                        />
                        <p className="text-xs text-slate-500 mt-1">This text will be highlighted in yellow for students</p>
                    </div>

                    {/* Answer Choices */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Answer Choices:</label>
                        <div className="space-y-3">
                            {formData.choices.map((choice, index) => (
                                <div key={index} className="border rounded-lg p-3 bg-slate-50">
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="flex items-center gap-3 flex-1">
                                            <input
                                                type="radio"
                                                name="correctIndex"
                                                value={index}
                                                checked={formData.correctIndex === index}
                                                onChange={handleCorrectIndexChange}
                                                className="text-blue-600"
                                            />
                                            <input
                                                type="text"
                                                value={choice}
                                                onChange={(e) => updateChoice(index, e.target.value)}
                                                className="flex-1 rounded-lg border px-3 py-2"
                                                placeholder={`Choice ${String.fromCharCode(65 + index)}`}
                                            />
                                        </div>
                                        {formData.choices.length > 2 && (
                                            <button
                                                type="button"
                                                onClick={() => removeChoice(index)}
                                                className="text-xs text-red-600 hover:text-red-800"
                                                aria-label="Remove answer choice"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium mb-1 text-slate-600">Highlight for this choice (Optional):</label>
                                        <input
                                            type="text"
                                            value={formData.answerHighlights[index]}
                                            onChange={(e) => updateAnswerHighlight(index, e.target.value)}
                                            className="w-full rounded-lg border px-3 py-2 text-sm"
                                            placeholder="Enter text to highlight in this answer choice"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        {formData.choices.length < 4 && (
                            <button
                                type="button"
                                onClick={addChoice}
                                className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                            >
                                + Add Answer Choice
                            </button>
                        )}
                    </div>

                    {/* Explanation */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Explanation (Optional):</label>
                        <textarea
                            value={formData.explanation}
                            onChange={handleFieldChange('explanation')}
                            className="w-full rounded-lg border px-3 py-2 h-20 resize-none"
                            placeholder="Explain why this answer is correct..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Explanation Image</label>
                        {isEditing && question?.id ? (
                            <>
                                {hasExplanationImage ? (
                                    <div className="mb-2">
                                        <img
                                            src={explanationImagePreviewUrl}
                                            alt="Explanation"
                                            className="max-h-56 rounded-lg border border-slate-200 shadow"
                                        />
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-500 mb-2">No explanation image uploaded.</p>
                                )}
                                {explanationImageError && (
                                    <p className="text-xs text-red-600 mb-2">{explanationImageError}</p>
                                )}
                                <div className="flex items-center gap-3">
                                    <label className={`inline-flex cursor-pointer items-center rounded-lg bg-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-300 ${isUploadingExplanationImage ? 'opacity-70 cursor-wait' : ''}`}>
                                        Upload Image
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleExplanationImageUpload}
                                            disabled={isUploadingExplanationImage}
                                        />
                                    </label>
                                    {hasExplanationImage && (
                                        <button
                                            type="button"
                                            onClick={handleExplanationImageRemove}
                                            disabled={isUploadingExplanationImage}
                                            className="text-sm text-red-600 hover:text-red-800"
                                        >
                                            Remove Image
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-slate-500 mt-2">PNG or JPG, up to 5 MB.</p>
                                {isUploadingExplanationImage && (
                                    <p className="text-xs text-slate-500 mt-1">Uploading...</p>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-slate-500">Save the question first to upload an explanation image.</p>
                        )}
                    </div>

                    {/* Form Actions */}
                    <div className="flex flex-wrap gap-3 pt-4">
                        <button
                            type="submit"
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                        >
                            {isEditing ? "Update Question" : "Add Question"}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-slate-200 text-slate-700 px-6 py-2 rounded-lg hover:bg-slate-300"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
